// frontend/src/contexts/ChatContext.tsx - COMPLETE FIXED VERSION

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  read: boolean;
}

interface ChatContextType {
  messages: Message[];
  isOpen: boolean;
  isTyping: boolean;
  error: string | null;
  isAuthenticated: boolean;
  sendMessage: (text: string) => Promise<void>;
  toggleChat: () => void;
  closeChat: () => void;
  resetChat: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const conversationIdRef = useRef(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const authToken = localStorage.getItem('authToken');
  const isAuthenticated = !!authToken;

  // ✅ Get API base URL from environment variable
  const API_BASE_URL = 'https://emailai-backend-658e.onrender.com/api';
  console.log('📡 Chat API URL:', API_BASE_URL);

  // ✅ COMPLETE FIXED SEND MESSAGE FUNCTION
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;

    // Add user message immediately
    const userMessageId = `msg_${Date.now()}_user`;
    setMessages(prev => [...prev, {
      id: userMessageId,
      content: text,
      sender: 'user',
      timestamp: new Date(),
      read: true
    }]);

    setIsTyping(true);
    setError(null);

    try {
      // ✅ FIXED: Use full backend URL from environment variable
      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          message: text,
          conversationId: conversationIdRef.current
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Read streaming response line by line (NDJSON)
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let aiMessageId = `msg_${Date.now()}_ai`;
      let aiContent = '';
      let lastUpdateTime = Date.now();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('✅ Stream complete');
          break;
        }

        // Decode chunk
        buffer += decoder.decode(value, { stream: true });

        // Split by newlines (NDJSON format)
        const lines = buffer.split('\n');

        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        // Process complete lines
        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const chunk = JSON.parse(line);

            console.log(`📡 Stream chunk [${chunk.status}]:`, chunk.progress, '%');

            // Accumulate AI response
            if (chunk.reply) {
              aiContent = chunk.reply;

              // Update UI with throttling (max 10 updates/sec)
              const now = Date.now();
              if (now - lastUpdateTime > 100) {
                setMessages(prev => {
                  const existing = prev.find(m => m.id === aiMessageId);
                  if (existing) {
                    // Update existing AI message
                    return prev.map(m =>
                      m.id === aiMessageId
                        ? { ...m, content: aiContent }
                        : m
                    );
                  } else {
                    // Create new AI message
                    return [...prev, {
                      id: aiMessageId,
                      content: aiContent,
                      sender: 'ai',
                      timestamp: new Date(),
                      read: false
                    }];
                  }
                });
                lastUpdateTime = now;
              }
            }

            // Handle errors
            if (chunk.status === 'error') {
              const errorMsg = chunk.reply || chunk.error || 'Unknown error';
              console.error('❌ AI Error:', errorMsg);
              setError(errorMsg);
              setIsTyping(false);
              return;
            }

            // Complete
            if (chunk.status === 'done') {
              console.log('✅ Chat message complete');
              // Final update with complete content
              setMessages(prev => {
                const existing = prev.find(m => m.id === aiMessageId);
                if (existing) {
                  return prev.map(m =>
                    m.id === aiMessageId
                      ? { ...m, content: aiContent }
                      : m
                  );
                } else {
                  return [...prev, {
                    id: aiMessageId,
                    content: aiContent,
                    sender: 'ai',
                    timestamp: new Date(),
                    read: false
                  }];
                }
              });
              setIsTyping(false);
              return;
            }

          } catch (parseError) {
            console.error('❌ JSON parse error:', parseError, 'Line:', line.substring(0, 100));
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.trim()) {
        try {
          const finalChunk = JSON.parse(buffer);
          if (finalChunk.reply) {
            aiContent = finalChunk.reply;
            setMessages(prev => {
              const existing = prev.find(m => m.id === aiMessageId);
              if (existing) {
                return prev.map(m =>
                  m.id === aiMessageId
                    ? { ...m, content: aiContent }
                    : m
                );
              } else {
                return [...prev, {
                  id: aiMessageId,
                  content: aiContent,
                  sender: 'ai',
                  timestamp: new Date(),
                  read: false
                }];
              }
            });
          }
        } catch (e) {
          console.error('Final chunk parse error:', e);
        }
      }

      setIsTyping(false);

    } catch (err) {
      console.error('❌ Chat error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
      setError(errorMessage);
      setIsTyping(false);
    }
  }, [authToken, API_BASE_URL]);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetChat = useCallback(() => {
    setMessages([]);
    setError(null);
    conversationIdRef.current = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const value: ChatContextType = {
    messages,
    isOpen,
    isTyping,
    error,
    isAuthenticated,
    sendMessage,
    toggleChat,
    closeChat,
    resetChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export default ChatContext;